// src/pages/BusinessPage.tsx — Site officiel Vanessa : entreprises et partenaires
import { Newspaper, HeartHandshake, Landmark, BookOpenCheck, Gauge, LayoutDashboard, Mail, MessageCircle, Share2 } from 'lucide-react';
import { Section, Grid, FeatureCard, CtaBand, PageHeader, Steps } from '@/components/site/blocks';
import { CONTACT_EMAIL } from '@/config/site';

const CHANNELS = [
    { icon: Mail, name: 'Email', text: 'Vanessa rédige et envoie des emails à vos clients, dans son ton, depuis votre propre adresse. Connexion via Gmail ou Outlook, jamais de mot de passe partagé.' },
    { icon: MessageCircle, name: 'WhatsApp', text: 'Elle répond à vos clients sur votre numéro WhatsApp Business, dans les délais autorisés par la plateforme. Nécessite un compte WhatsApp Business officiel.' },
    { icon: Share2, name: 'Réseaux sociaux', text: 'Elle répond aux commentaires de vos publications Facebook ou Instagram, avec votre voix. Connexion via votre page professionnelle Meta.' },
];

export default function BusinessPage() {
    const mail = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Partenariat Vanessa')}`;

    return (
        <>
            <PageHeader
                eyebrow="Entreprises et partenaires"
                title="Vanessa au service de ceux qui parlent à beaucoup de monde."
                intro="Médias, associations, institutions : reliez Vanessa à votre expertise pour que les conversations avec votre public restent vivantes et factuelles."
            >
                <a href={mail} className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-brand px-6 py-3 font-display font-semibold text-ink hover:bg-brand-hover">
                    Devenir partenaire
                </a>
            </PageHeader>

            <Section title="Les connecteurs partenaires" intro="Un connecteur relie une conversation avec Vanessa à un partenaire. Une pastille l’indique en haut du chat.">
                <Grid cols={3}>
                    <FeatureCard icon={Newspaper} title="Médias">Vanessa discute de vos sujets à partir de vos contenus et renvoie vers vos articles.</FeatureCard>
                    <FeatureCard icon={HeartHandshake} title="Associations">Un premier contact bienveillant, avec des ressources fiables et vérifiées.</FeatureCard>
                    <FeatureCard icon={Landmark} title="Institutions">Une information claire et accessible, dans un langage qui parle à tout le monde.</FeatureCard>
                </Grid>
            </Section>

            <Section tone="sand" title="Comment ça fonctionne">
                <div className="grid gap-10 lg:grid-cols-2">
                    <Grid cols={2}>
                        <FeatureCard icon={BookOpenCheck} title="Base de connaissances">
                            Vous fournissez les informations de référence. Elles sont validées avant d’être activées.
                        </FeatureCard>
                        <FeatureCard icon={Gauge} title="Volume offert">
                            Selon le partenariat, un quota de tokens peut être offert, voire sans limite.
                        </FeatureCard>
                        <FeatureCard icon={LayoutDashboard} title="Espace partenaire">
                            Suivez la consommation de votre connecteur dans votre espace dédié.
                        </FeatureCard>
                    </Grid>
                    <div className="rounded-3xl bg-white p-6">
                        <p className="mb-4 font-display text-xl font-semibold text-ink">Ce qui ne change pas</p>
                        <Steps
                            items={[
                                { title: 'Son ton', text: 'Vanessa garde sa personnalité, quel que soit le partenaire.' },
                                { title: 'Ses garde-fous', text: 'Le contexte du partenaire s’ajoute, il ne remplace jamais ses limites de sécurité.' },
                                { title: 'La rigueur', text: 'Sur le sujet du partenaire, elle reste factuelle.' },
                            ]}
                        />
                    </div>
                </div>
            </Section>

            <Section
                title="Vanessa pour votre entreprise"
                intro="Bientôt disponible. Le ton de Vanessa, au service de votre propre activité, pour parler à vos clients là où ils sont déjà."
            >
                <Grid cols={3}>
                    {CHANNELS.map((c) => (
                        <FeatureCard key={c.name} icon={c.icon} title={c.name}>{c.text}</FeatureCard>
                    ))}
                </Grid>
                <p className="mt-6 max-w-3xl rounded-2xl bg-brand-tint p-5 text-base text-ink">
                    <strong>Pourquoi ce n’est pas encore actif ?</strong> Nous préférons d’abord bien faire ce qui existe déjà, et vérifier que l’idée trouve preneur, avant un chantier plus lourd : chaque canal a ses propres règles d’approbation, notamment WhatsApp et les réseaux sociaux.
                </p>
            </Section>

            <CtaBand
                title="Parlons de votre projet"
                text="Dites-nous qui vous êtes et ce que vous imaginez."
                primary={{ label: 'Nous écrire', href: mail }}
                secondary={{ label: 'Voir l’API', to: '/developpeurs', variant: 'secondary' }}
            />
        </>
    );
}
